import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Pagination,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Euro as EuroIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';

const ProductSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [skuTerm, setSkuTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (newPage = 1) => {
    if (!searchTerm && !skuTerm) {
      setError('Voer een zoekterm of SKU in');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setPage(newPage);

      const params = {
        page: newPage,
        per_page: 12
      };

      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (skuTerm) {
        params.sku = skuTerm;
      }

      const response = await apiService.getProducts(params);
      setProducts(response.data || []);
      setHasSearched(true);
      
    } catch (err) {
      setError(err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(1);
    }
  };

  const getStockStatusInfo = (product) => {
    if (product.manage_stock) {
      const stockQuantity = product.stock_quantity || 0;
      if (stockQuantity > 0) {
        return { label: `${stockQuantity} op voorraad`, color: 'success' };
      } else {
        return { label: 'Uitverkocht', color: 'error' };
      }
    } else {
      return { 
        label: product.stock_status === 'instock' ? 'Op voorraad' : 'Uitverkocht', 
        color: product.stock_status === 'instock' ? 'success' : 'error' 
      };
    }
  };

  const ProductCard = ({ product }) => {
    const stockInfo = getStockStatusInfo(product);
    
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {product.images && product.images.length > 0 && (
          <CardMedia
            component="img"
            height="200"
            image={product.images[0].src}
            alt={product.name}
            sx={{ objectFit: 'cover' }}
          />
        )}
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography gutterBottom variant="h6" component="div" noWrap>
            {product.name}
          </Typography>
          
          <Box mb={1}>
            <Typography variant="body2" color="textSecondary">
              SKU: {product.sku || 'Geen SKU'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Product ID: {product.id}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="primary">
              €{parseFloat(product.price || 0).toFixed(2)}
            </Typography>
            <Chip 
              label={stockInfo.label}
              color={stockInfo.color}
              size="small"
            />
          </Box>

          {product.categories && product.categories.length > 0 && (
            <Box mb={1}>
              <Typography variant="body2" color="textSecondary">
                Categorieën:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {product.categories.slice(0, 3).map((category) => (
                  <Chip
                    key={category.id}
                    label={category.name}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {product.short_description && (
            <Typography 
              variant="body2" 
              color="textSecondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Producten Zoeken
      </Typography>

      {/* Search Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Zoek op titel"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Bijv. iPhone, laptop, shirt..."
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Zoek op SKU"
              value={skuTerm}
              onChange={(e) => setSkuTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InventoryIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Bijv. IPHONE-13-BLK"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => handleSearch(1)}
              disabled={loading || (!searchTerm && !skuTerm)}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {loading ? 'Zoeken...' : 'Zoeken'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {hasSearched && !loading && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            {products.length} {products.length === 1 ? 'product gevonden' : 'producten gevonden'}
          </Typography>
        </Box>
      )}

      {/* Products Grid */}
      {products.length > 0 && (
        <>
          <Grid container spacing={3}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <ProductCard product={product} />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={10} // WooCommerce doesn't always provide total pages, so we limit to 10
              page={page}
              onChange={(e, newPage) => handleSearch(newPage)}
              color="primary"
              size="large"
              disabled={loading}
            />
          </Box>
        </>
      )}

      {/* No Results */}
      {hasSearched && !loading && products.length === 0 && !error && (
        <Box textAlign="center" py={8}>
          <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Geen producten gevonden
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Probeer andere zoektermen of controleer de spelling
          </Typography>
        </Box>
      )}

      {/* Initial State */}
      {!hasSearched && !loading && (
        <Box textAlign="center" py={8}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Zoek producten op titel of SKU
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Voer een zoekterm in het veld hierboven en druk op zoeken
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProductSearch; 